import { Flex, notification, Tooltip as ATooltip } from 'antd';
import BigNumber from 'bignumber.js';
import { Moon, Sun, Settings, Globe } from 'lucide-react';
import React, { ReactNode, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { VirtualBetaBlackLogo, VirtualBetaWhiteLogo } from 'src/assets/icons';
import MenuIcon from 'src/assets/icons/navbar/MenuIcon';
import { Skeleton } from 'src/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { PATHS } from 'src/constants/paths';
import { useSocket } from 'src/contexts/SocketContext';
import { calculateTotalPNL } from 'src/helpers';
import { formatRoundFloorDisplay } from 'src/helpers/formatNumber';
import { setStorageTheme } from 'src/helpers/storage';
import { useDisclosure } from 'src/hooks/useDisclosure';
import useWindowSize from 'src/hooks/useWindowSize';
import { THEME_MODE } from 'src/interfaces/theme';
import { useAuth } from 'src/libs/rainbow';
import { UserPositionDto } from 'src/services/order/get-position';
import { useGetUserInfo } from 'src/services/user/get-user-info';
import { useAppDispatch, useAppSelector } from 'src/store';
import { setModalConfirmAdjustLeverage, setModalTerms } from 'src/store/actions/modal';
import { setTheme } from 'src/store/actions/theme';
import { Button } from '../../ui/button';
import ModalConfirmAdjustLeverage from './ModalConfirmAdjustLeverage';
import ModalTerms from './ModalTerms';
import './styles.scss';
import SubMenu from './SubMenu';
import { mobileMenuBreakPoint } from 'src/constants/exchange';
import { twJoin } from 'tailwind-merge';
import { useAppTranslation, useLanguageSwitcher } from 'src/i18n/hooks';
import ConnectWalletButton from '../../02.buttons/ConnectWalletButton';
import ShowQRButton from '../../02.buttons/ShowQRButton';
import DepositModalV2 from './DepositModalV2';
import { useWalletSelector } from '@near-wallet-selector/react-hook';
interface Props {
  isOpenMenuMobile: boolean;
  setIsOpenMenuMobile: (bool: boolean) => void;
  userPositionData: UserPositionDto[] | undefined;
}

function BalanceDisplay({
  isAuthenticated,
  userBalance,
  isBalanceSocketInitialized,
  theme,
  userPositionData,
  userAssets,
  totalUnrealizedPnl,
}: {
  isAuthenticated: boolean;
  userBalance: number;
  isBalanceSocketInitialized: boolean;
  theme: THEME_MODE;
  userPositionData: UserPositionDto[] | undefined;
  userAssets: Array<{ tokenName: string; balance: number }>;
  totalUnrealizedPnl: number;
}): ReactNode {
  const { t } = useAppTranslation('common');

  if (!isAuthenticated) {
    return null;
  }

  if (!isBalanceSocketInitialized) {
    return <Skeleton className={`w-24 h-6 bg-gray-200`} />;
  }

  const balance = userBalance > 0 ? userBalance : '0';
  const textColor = theme === THEME_MODE.DARK ? 'text-white' : 'text-black';

  const balanceWithPnl = new BigNumber(balance || 0).plus(
    calculateTotalPNL(userPositionData ?? []),
  );

  const assetItems = userAssets
    .filter((asset) => asset.balance > 0)
    .map((asset, index) => (
      <div key={index} className="flex justify-between mb-1">
        <span>{asset.tokenName}</span>
        <span className="ml-2">{formatRoundFloorDisplay(asset.balance)}</span>
      </div>
    ));

  const formatPnl = (value: number) => {
    const formattedValue = formatRoundFloorDisplay(value);
    return value >= 0 ? `+${formattedValue}` : formattedValue;
  };
  const pnlValue = totalUnrealizedPnl;
  const pnlColor = pnlValue >= 0 ? 'text-green-500' : 'text-red-500';

  const tooltipBg = theme === THEME_MODE.DARK ? 'bg-[#1f1f1f]' : 'bg-white';
  const tooltipBorder = theme === THEME_MODE.DARK ? 'border-gray-700' : 'border-gray-200';
  const tooltipText = theme === THEME_MODE.DARK ? 'text-white' : 'text-black';
  const tooltipHeaderBorder = theme === THEME_MODE.DARK ? 'border-gray-700' : 'border-gray-300';
  const tooltipMutedText = theme === THEME_MODE.DARK ? 'text-gray-400' : 'text-gray-500';
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="py-[10px]">
            <h3 className={`${textColor} cursor-default`}>
              {formatRoundFloorDisplay(balanceWithPnl)} USD
            </h3>
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          align="end"
          className={`p-3 rounded-md shadow-md border ${tooltipBg} ${tooltipBorder} min-w-[180px] z-50 ${tooltipText}`}
          sideOffset={5}
        >
          <p className={`font-medium border-b ${tooltipHeaderBorder} pb-1 mb-2`}>
            {t('data.assets')}
          </p>
          <div className="text-xs">
            {assetItems.length > 0 ? (
              <>
                {assetItems}
                <div
                  className={`flex justify-between mt-2 pt-1 border-t ${tooltipHeaderBorder} ${pnlColor}`}
                >
                  <span>{t('data.pnl')}</span>
                  <span className="ml-2">{formatPnl(pnlValue)}</span>
                </div>
              </>
            ) : (
              <p className={tooltipMutedText}>{t('data.noAssetsAvailable')}</p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

const AppHeader: React.FC<Props> = ({
  isOpenMenuMobile,
  setIsOpenMenuMobile,
  userPositionData,
}) => {
  const userTheme = useSelector((state: any) => state.theme);
  const { isDesktop, width } = useWindowSize();
  const dispatch = useAppDispatch();
  const { isUnauthenticated, isAuthenticated, isEthereumWallet } = useAuth();
  const { signedAccountId } = useWalletSelector();

  const { userBalance, userAssets, totalUnrealizedPnl, isBalanceSocketInitialized } = useSocket();
  const [depositModalOpen, depositModalActions] = useDisclosure();
  const darkMode = userTheme === 'dark';
  const { t } = useAppTranslation('navbar');
  const { currentLanguage, availableLanguages, switchLanguage, getLanguageDisplayName } =
    useLanguageSwitcher();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const userId = localStorage.getItem('userId');
  const { data: userInfo } = useGetUserInfo(userId ?? '');
  const modalTerms = useAppSelector((state) => state.modal.modalTerms);
  const modalConfirmAdjustLeverage = useAppSelector(
    (state) => state.modal.modalConfirmAdjustLeverage,
  );

  const handleChangeTheme = () => {
    if (userTheme === THEME_MODE.DARK) {
      dispatch(setTheme(THEME_MODE.LIGHT));
      setStorageTheme(THEME_MODE.LIGHT.toLocaleLowerCase());
    } else {
      dispatch(setTheme(THEME_MODE.DARK));
      setStorageTheme(THEME_MODE.DARK.toLocaleLowerCase());
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const renderRightMenuAction = () => {
    return (
      <div className="flex items-center ml-2 relative" ref={dropdownRef}>
        <button
          className="p-2 rounded-md transition-colors"
          style={{
            color: darkMode ? '#ffffff' : '#000000',
            backgroundColor: 'transparent',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = darkMode ? '#4b5563' : '#f3f4f6';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        >
          <Settings
            className="w-5 h-5"
            style={{
              color: darkMode ? '#ffffff' : '#000000',
            }}
          />
        </button>

        {isDropdownOpen && (
          <div
            className="absolute top-full right-0 mt-2 w-48 z-50 rounded-md shadow-lg border"
            style={{
              backgroundColor: darkMode ? '#374151' : '#ffffff',
              borderColor: darkMode ? '#4b5563' : '#e5e7eb',
              color: darkMode ? '#f9fafb' : '#111827',
            }}
          >
            {/* Settings Header */}
            <div
              className="px-2 py-1.5 text-sm font-semibold border-b"
              style={{
                color: darkMode ? '#f9fafb' : '#111827',
                borderBottomColor: darkMode ? '#4b5563' : '#e5e7eb',
              }}
            >
              {t('Settings')}
            </div>

            {/* Theme Switcher */}
            <div
              className="px-2 py-1.5 text-sm cursor-pointer hover:opacity-80 transition-opacity"
              style={{
                color: darkMode ? '#f9fafb' : '#111827',
                backgroundColor: 'transparent',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = darkMode ? '#4b5563' : '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              onClick={() => {
                handleChangeTheme();
                setIsDropdownOpen(false);
              }}
            >
              <div className="flex items-center gap-2">
                {userTheme === THEME_MODE.DARK ? (
                  <Sun className="w-4 h-4" style={{ color: darkMode ? '#f9fafb' : '#111827' }} />
                ) : (
                  <Moon className="w-4 h-4" style={{ color: darkMode ? '#f9fafb' : '#111827' }} />
                )}
                <span>{userTheme === THEME_MODE.DARK ? t('LightMode') : t('DarkMode')}</span>
              </div>
            </div>

            {/* Separator */}
            <div
              className="h-px my-1 mx-1"
              style={{ backgroundColor: darkMode ? '#4b5563' : '#e5e7eb' }}
            />
            {isAuthenticated && (
              <>
                {/* Language Header */}
                <div
                  className="px-2 py-1.5 text-sm font-semibold flex items-center gap-2"
                  style={{ color: darkMode ? '#f9fafb' : '#111827' }}
                >
                  <Globe className="w-4 h-4" style={{ color: darkMode ? '#f9fafb' : '#111827' }} />
                  {t('Language')}
                </div>

                {/* Language Options */}
                {availableLanguages.map((language) => (
                  <div
                    key={language}
                    className="px-2 py-1.5 text-sm cursor-pointer transition-opacity"
                    style={{
                      color:
                        currentLanguage === language
                          ? darkMode
                            ? '#93c5fd'
                            : '#1d4ed8'
                          : darkMode
                            ? '#f9fafb'
                            : '#111827',
                      backgroundColor:
                        currentLanguage === language
                          ? darkMode
                            ? '#1e3a8a'
                            : '#eff6ff'
                          : 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      if (currentLanguage !== language) {
                        e.currentTarget.style.backgroundColor = darkMode ? '#4b5563' : '#f3f4f6';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentLanguage !== language) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      } else {
                        e.currentTarget.style.backgroundColor = darkMode ? '#1e3a8a' : '#eff6ff';
                      }
                    }}
                    onClick={() => {
                      switchLanguage(language);
                      setIsDropdownOpen(false);
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <span className="ml-6">{getLanguageDisplayName(language)}</span>
                      {currentLanguage === language && (
                        <span style={{ color: darkMode ? '#60a5fa' : '#2563eb' }}>✓</span>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  const handleClickLogo = () => {
    window.location.replace(PATHS.default());
  };

  const handleDepositModal = () => {
    if (isUnauthenticated) {
      notification.info({
        message: t('message'),
        description: t('description'),
        placement: 'bottomRight',
        style: { fontFamily: 'Poppins' },
      });
    } else if (isEthereumWallet) {
      depositModalActions.open();
    } else {
      notification.info({
        message: t('message'),
        description: 'Please connect EVM wallet and deposit USDC on Arbitrum to trade',
        placement: 'bottomRight',
        style: { fontFamily: 'Poppins' },
      });
    }
  };

  useEffect(() => {
    if (isAuthenticated && userInfo?.is_accept_terms === false) {
      dispatch(setModalTerms(true));
    }
  }, [isAuthenticated, userInfo, dispatch]);

  return (
    <div
      className={twJoin(
        `${userTheme} flex border-b-[1px]  w-[100%] z-30 border-t-0 border-l-0 border-r-0 shadow-lg border-dark-1/70 dark:border-[#242424] justify-between items-center p-4  font-semibold sticky top-0`,
        darkMode ? 'bg-[#111215]' : 'bg-white',
      )}
    >
      <div className="flex items-center justify-between gap-[40px]">
        <button
          className="flex items-center justify-center cursor-pointer"
          onClick={() => handleClickLogo()}
        >
          <img
            src={userTheme === THEME_MODE.DARK ? VirtualBetaWhiteLogo : VirtualBetaBlackLogo}
            alt="frac logo"
            className="w-[137px]"
          />
        </button>

        <div className="flex">{(isDesktop || width > mobileMenuBreakPoint) && <SubMenu />}</div>
      </div>

      {width > mobileMenuBreakPoint ? (
        <div className="flex items-center justify-around">
          <Flex gap={12} align="center">
            {isEthereumWallet ? (
              <Button
                className="bg-[#00632B] py-4 min-w-[100px] text-white hover:bg-[#00682B]"
                onClick={handleDepositModal}
                id="deposit-button"
              >
                {t('Deposit')}
              </Button>
            ) : (
              <ATooltip title="Deposit USDC on Arbitrum to start trading">
                <Button
                  className="bg-[#00632B] py-4 min-w-[100px] text-white hover:bg-[#00682B]"
                  onClick={handleDepositModal}
                  id="deposit-button"
                >
                  {t('Deposit')}
                </Button>
              </ATooltip>
            )}

            <ConnectWalletButton />
            {isAuthenticated && isEthereumWallet ? <ShowQRButton /> : null}
            {isEthereumWallet ? (
              <BalanceDisplay
                isAuthenticated={isAuthenticated}
                userBalance={userBalance}
                isBalanceSocketInitialized={isBalanceSocketInitialized}
                theme={userTheme}
                userPositionData={userPositionData}
                userAssets={userAssets}
                totalUnrealizedPnl={totalUnrealizedPnl}
              />
            ) : (
              <BalanceDisplay
                isAuthenticated={!!signedAccountId}
                userBalance={0}
                isBalanceSocketInitialized={true}
                theme={userTheme}
                userPositionData={[]}
                userAssets={[]}
                totalUnrealizedPnl={0}
              />
            )}
          </Flex>

          {renderRightMenuAction()}
        </div>
      ) : (
        <span
          className="menu-icon-mobile"
          onClick={() => {
            setIsOpenMenuMobile(!isOpenMenuMobile);
          }}
        >
          <MenuIcon color={'#878787'} isOpen={isOpenMenuMobile} />
        </span>
      )}

      <DepositModalV2
        key={1}
        open={depositModalOpen}
        onClose={depositModalActions.close}
        onDepositSettle={depositModalActions.close}
      />
      <ModalTerms isOpen={modalTerms} />
      <ModalConfirmAdjustLeverage
        isOpen={modalConfirmAdjustLeverage}
        onClose={() => dispatch(setModalConfirmAdjustLeverage(false))}
      />
    </div>
  );
};

export default React.memo(AppHeader);
